import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { Box } from "@mui/material";
import { beerPalette } from "../../../../theme/Colours";
import colors from "../../../../theme/Colours";

import {
  SalesAnalyticsCardLabel,
  SalesAnalyticsContentBox,
  StyledSalesAnalyticsCard,
} from "../styles";

type BeerTypeData = {
  beer_type: string;
  sales_qty: string | number;
  sales_percent: string | number;
  unit: string;
};

type PeriodData = {
  period: "current" | "one" | "two" | string;
  name: string;
  values: BeerTypeData[];
};

type ChartRow = {
  name: string;
  shortName: string;
  qty: number;
  pct: number;
  period1Qty: number;
  period1Pct: number;
  period2Qty: number;
  period2Pct: number;
  unit: string;
};

const TOOLTIP = {
  root: "max-width:220px;font-size:10px;line-height:14px;color:#3b4856;",
  title: "font-size:10px;font-weight:600;margin-bottom:6px;white-space:normal;",
  row: "font-size:10px;line-height:14px;margin-bottom:2px;",
  value: "font-size:10px;margin-bottom:5px;",
  dot: "display:inline-block;width:7px;height:7px;border-radius:50%;margin-right:5px;",
};

const truncateLabel = (label: string, maxLength = 14) => {
  if (!label) return "";
  return label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;
};

const wrapTooltipName = (name: string) => {
  if (!name) return "";

  return name
    .split(" ")
    .reduce<string[]>(
      (lines, word) => {
        const lastLine = lines[lines.length - 1] || "";

        if ((lastLine + " " + word).trim().length <= 28) {
          lines[lines.length - 1] = (lastLine + " " + word).trim();
        } else if (lines.length < 2) {
          lines.push(word);
        } else {
          lines[1] = `${lines[1]}...`;
        }

        return lines;
      },
      [""]
    )
    .slice(0, 2)
    .join("<br />");
};

const renderDot = (color: string) =>
  `<span style="${TOOLTIP.dot}background:${color};"></span>`;

const renderSingleTooltip = (row: ChartRow) => `
  <div style="${TOOLTIP.root};max-width:200px;">
    <div style="${TOOLTIP.title}">
      ${wrapTooltipName(row.name)}
    </div>
    <div style="${TOOLTIP.row}">${row.qty}${row.unit} sold</div>
    <div style="${TOOLTIP.row}">${row.pct}% sales</div>
  </div>
`;

const renderComparisonTooltip = (
  row: ChartRow,
  period1Label = "Period 1",
  period2Label = "Period 2"
) => `
  <div style="${TOOLTIP.root}">
    <div style="${TOOLTIP.title}">
      ${wrapTooltipName(row.name)}
    </div>

    <div style="${TOOLTIP.row}">
      ${renderDot(colors.linePlotBlue)}
      ${period1Label}: ${row.period1Qty}${row.unit} sold
    </div>
    <div style="${TOOLTIP.value}">
      ${row.period1Pct}% sales
    </div>

    <div style="${TOOLTIP.row}">
      ${renderDot(colors.barPlotOrange)}
      ${period2Label}: ${row.period2Qty}${row.unit} sold
    </div>
    <div style="${TOOLTIP.row}">
      ${row.period2Pct}% sales
    </div>
  </div>
`;

export default function SalesByBeerType({
  comparisonFilterEnabled,
  data,
  periodValues,
}: {
  comparisonFilterEnabled?: boolean;
  data: {
    key: string;
    name: string;
    unit: string;
    details: PeriodData[];
  };
  periodValues?: { [key: string]: string };
}) {
  const period1Label = (periodValues?.["Period 1"] ?? "Period 1").replace(
    /\n/g,
    " "
  );
  const period2Label = (periodValues?.["Period 2"] ?? "Period 2").replace(
    /\n/g,
    " "
  );
  const chartRows = useMemo<ChartRow[]>(() => {
    const details = data?.details || [];

    const current = details.find((d) => d.period === "current")?.values || [];
    const period1 = details.find((d) => d.period === "one")?.values || [];
    const period2 = details.find((d) => d.period === "two")?.values || [];

    const allNames = comparisonFilterEnabled
      ? [
          ...period1.map((v) => v.beer_type),
          ...period2.map((v) => v.beer_type),
        ].filter((v, i, arr) => arr.indexOf(v) === i)
      : current.map((v) => v.beer_type);

    return allNames.map((name) => {
      const curr = current.find((v) => v.beer_type === name);
      const p1 = period1.find((v) => v.beer_type === name);
      const p2 = period2.find((v) => v.beer_type === name);

      return {
        name,
        shortName: truncateLabel(name),
        qty: Number(curr?.sales_qty) || 0,
        pct: Number(curr?.sales_percent) || 0,
        period1Qty: Number(p1?.sales_qty) || 0,
        period1Pct: Number(p1?.sales_percent) || 0,
        period2Qty: Number(p2?.sales_qty) || 0,
        period2Pct: Number(p2?.sales_percent) || 0,
        unit: curr?.unit || p1?.unit || p2?.unit || "L",
      };
    });
  }, [data, comparisonFilterEnabled]);

  const tooltip = useMemo(
    () => ({
      trigger: "axis",
      confine: true,
      appendToBody: true,
      axisPointer: {
        type: "shadow",
      },
      backgroundColor: colors.background2,
      borderColor: colors.greyBorder,
      borderWidth: 1,
      padding: 8,
      extraCssText:
        "z-index:9999;border-radius:6px;box-shadow:0px 2px 8px rgba(0,0,0,0.15);",
      textStyle: {
        color: colors.salesAnalyticsCardLabelText,
        fontSize: 10,
        lineHeight: 14,
      },
      formatter: (params: any[]) => {
        const row = chartRows[params?.[0]?.dataIndex];
        if (!row) return "";

        return comparisonFilterEnabled
          ? renderComparisonTooltip(row, period1Label, period2Label)
          : renderSingleTooltip(row);
      },
    }),
    [chartRows, comparisonFilterEnabled, period1Label, period2Label]
  );

  const option = useMemo(() => {
    const commonAxisStyles = {
      axisTick: { show: false },
      axisLabel: {
        fontSize: 9,
        color: colors.salesAnalyticsCardLabelText,
        interval: 0,
      },
    };

    if (comparisonFilterEnabled) {
      return {
        tooltip,
        legend: {
          bottom: 0,
          left: "center",
          icon: "circle",
          itemWidth: 9,
          itemHeight: 9,
          itemGap: 16,
          textStyle: {
            fontSize: 10,
            color: colors.salesAnalyticsCardLabelText,
          },
        },
        grid: {
          top: 18,
          left: 45,
          right: 16,
          bottom: 38,
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: chartRows.map((r) => r.shortName),
          ...commonAxisStyles,
        },
        yAxis: {
          type: "value",
          name: "Sales Volume (Litres)",
          nameLocation: "middle",
          nameGap: 32,
          axisLabel: {
            fontSize: 9,
            color: colors.salesAnalyticsCardLabelText,
          },
          splitLine: {
            lineStyle: {
              color: colors.greyBorder,
            },
          },
        },
        series: [
          {
            name: period1Label,
            type: "bar",
            data: chartRows.map((r) => r.period1Qty),
            barMaxWidth: 32,
            itemStyle: {
              color: colors.linePlotBlue,
              borderRadius: [4, 4, 0, 0],
            },
          },
          {
            name: period2Label,
            type: "bar",
            data: chartRows.map((r) => r.period2Qty),
            barMaxWidth: 32,
            itemStyle: {
              color: colors.barPlotOrange,
              borderRadius: [4, 4, 0, 0],
            },
          },
        ],
      };
    }

    return {
      tooltip,
      grid: {
        top: 18,
        left: 45,
        right: 16,
        bottom: 28,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: chartRows.map((r) => r.shortName),
        ...commonAxisStyles,
      },
      yAxis: {
        type: "value",
        name: "Sales Volume (L)",
        nameLocation: "middle",
        nameGap: 32,
        axisLabel: {
          fontSize: 9,
          color: colors.salesAnalyticsCardLabelText,
        },
        splitLine: {
          lineStyle: {
            color: colors.greyBorder,
          },
        },
      },
      series: [
        {
          name: "Sales Volume",
          type: "bar",
          data: chartRows.map((r) => r.qty),
          barMaxWidth: 32,
          itemStyle: {
            color: (params: any) =>
              beerPalette[params.dataIndex % beerPalette.length],
            borderRadius: [4, 4, 0, 0],
          },
        },
      ],
    };
  }, [chartRows, comparisonFilterEnabled, tooltip, period1Label, period2Label]);

  return (
    <StyledSalesAnalyticsCard>
      <SalesAnalyticsCardLabel>{data?.name}</SalesAnalyticsCardLabel>

      <SalesAnalyticsContentBox>
        <Box sx={{ width: "100%", height: 290 }}>
          <ReactECharts
            option={option}
            style={{ width: "100%", height: "100%" }}
            notMerge
            lazyUpdate
          />
        </Box>
      </SalesAnalyticsContentBox>
    </StyledSalesAnalyticsCard>
  );
}
